import { Box, useTheme } from '@mui/material';
import { EntityName } from '@provision/entity-types/dist/entity.types';
import { EventHubEntityChangePayloadObject } from '@provision/event-hub-utils';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isNullish } from 'remeda';
import { BlockContainer, BlockContent, MetaTags } from 'src/components';
import {
  CustomViewEntity,
  CustomViewFormConditionItem,
  CustomViewFormModel,
  useCustomViewEntityConfig,
} from 'src/graphql/entities/custom-view';
import { ConversationAttribute } from 'src/graphql/entities/custom-view/custom-view-config/conversation';
import { prepareSortAttributes } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { AnySearchableEntity, useSearch } from 'src/graphql/entities/search/use-search';
import { useSelectedRecord } from 'src/graphql/entities/search/use-selected-record';
import { ConditionOperator, EntityNames } from 'src/graphql/types';
import { CONVERSATIONS_VIEW_WRAPPER_STYLES } from 'src/pages/CustomViews/custom-views-selected-view/constants';
import { useCustomViewState } from 'src/pages/CustomViews/hooks/use-custom-view-state';
import { RecordsGridLayout } from 'src/pages/CustomViews/records-grid-layout';
import { useGlobalStore } from 'src/store';
import { useOnDeleteSubscription, useScrollPaginateHandler } from 'src/utils/hooks';
import { useRouteParams } from 'src/utils/routing';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';
import { capitalizeString } from 'src/utils/strings';
import { INITIAL_GRID_WIDTH_WITHOUT_PANEL, SEARCH_RECORDS_CHUNK_LIMIT } from './constants';
import { GenericModalSet } from './generic-modal-set';
import { GenericSidePanel } from './generic-side-panel';
import { SearchBar } from './search-bar';
import { TabsBar } from './tabs-bar';
import { useSearchTabs } from './use-search-tabs';

export const Search = () => {
  const { navigate } = useNavigateRoute();
  const theme = useTheme();
  const { tabs } = useSearchTabs();

  const { entityName: rawEntityName } = useRouteParams(UrlParams.entityName);
  const entityName = rawEntityName as CustomViewEntity;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q') ?? '';
  // seeded from ?q= so the in-nav compact search can expand into this page
  // carrying its query over; kept in sync below for deep links / reloads
  const [searchTerm, setSearchTerm] = useState(() => queryParam);

  // re-syncs when ?q= changes while the page is already mounted — e.g. expanding
  // the compact search again with a new term. Functional update is a no-op when
  // the value already matches (typing here writes ?q= itself), so no loop.
  useEffect(() => {
    setSearchTerm((current) => (current === queryParam ? current : queryParam));
  }, [queryParam]);
  const [record, setRecord] = useState<AnySearchableEntity | null>(null);

  // lets a result be opened directly via URL (e.g. a Project clicked in the
  // compact search lands here with its side panel peeked). The full record is
  // fetched by useSelectedRecord off this id — we deliberately do NOT seed a
  // partial { id } into `record`, since the side panels read full fields.
  const recordIdParam = searchParams.get('recordId') ?? '';

  const [additionalFilter, setAdditionalFilter] = useState<CustomViewFormConditionItem | null>(
    null,
  );

  const { attributesList, filterAttributesMap } = useCustomViewEntityConfig(entityName);

  const customViewData = useMemo(
    () => ({
      columns: attributesList.filter((i) => i.default),
    }),
    [attributesList],
  );

  const {
    isQueryBuilderOpen,
    filters: rawFilters,
    columns,
    formMethods,
    mappedConditions,
    defaultPartnerFilter,
    sort,
    resetToDefault,
    handleFiltersUpdate,
    handleColumnUpdate,
    handleColumnsUpdate,
    handleSortChange,
    toggleQueryBuilder,
  } = useCustomViewState({
    id: getSearchViewId(entityName),
    customView: customViewData,
    entity: entityName,
  });

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const sortApiParams = useMemo(() => {
    if (!sort) {
      return;
    }

    return prepareSortAttributes(sort);
  }, [sort]);

  const availableFilters = { map: filterAttributesMap };

  const { records, loadMore, hasMore, totalRecords, isLoadingMore, isLoading } =
    useSearch({
      filter: mappedConditions,
      entityName,
      searchTerm,
      sort: sortApiParams,
      limit: SEARCH_RECORDS_CHUNK_LIMIT,
      showResultsWhenEmptySearchTerm: false,
    }) ?? {};

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
  });

  useEffect(() => {
    if (!columns?.length && attributesList?.length) {
      handleColumnsUpdate(attributesList.filter((i) => i.default));
    }
  }, [attributesList, columns, handleColumnsUpdate]);

  useEffect(() => {
    if (isNullish(selectedPartnerId) && defaultPartnerFilter) {
      setAdditionalFilter({
        id: ConversationAttribute.partnerId,
        value: [
          {
            id: String(defaultPartnerFilter.id),
            value: String(defaultPartnerFilter.id),
            label: defaultPartnerFilter.businessName,
          },
        ],
        operator: ConditionOperator.In,
        field: availableFilters.map.get(ConversationAttribute.partnerId),
      });
    }
  }, [availableFilters.map, defaultPartnerFilter, selectedPartnerId, setAdditionalFilter]);

  // while we store record in useState, we can use it only as a fallback
  // for real usage we still have to use reactive record connected to apollo cache
  // otherwise the record will be static
  const { selectedRecord: rawSelectedRecord } = useSelectedRecord({
    recordId: record?.id ?? recordIdParam,
    entityName,
  });

  const selectedRecord = rawSelectedRecord || record;

  const setSelectedEntity = useCallback(
    (entity: string, replace?: boolean) => {
      // carry the query across entities but drop recordId — a selected record
      // belongs to the entity it was opened from
      const nextParams = new URLSearchParams();
      const q = searchParams.get('q');
      if (q) {
        nextParams.set('q', q);
      }

      navigate(
        {
          pathname: portalPaths.search(entity),
          search: nextParams.toString(),
        },
        { replace },
      );
      setRecord(null);
    },
    [navigate, searchParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setSearchParams(
        (current) => {
          const nextParams = new URLSearchParams(current);

          if (value) {
            nextParams.set('q', value);
          } else {
            nextParams.delete('q');
          }

          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleRecordClose = useCallback(() => {
    setRecord(null);
    // drop recordId so a reload/back doesn't re-open the closed record
    if (searchParams.has('recordId')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('recordId');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleRecordClick = useCallback((_: string, c: AnySearchableEntity) => {
    setRecord(c);
  }, []);

  useOnDeleteSubscription({
    entityName: capitalizeString(entityName || '') as EntityName,
    filter: useCallback(
      (message: EventHubEntityChangePayloadObject) =>
        message.previousAttributes.id === selectedRecord?.id,
      [selectedRecord?.id],
    ),
    callback: handleRecordClose,
  });

  const filters = useMemo(() => {
    if (!additionalFilter || rawFilters?.rules?.some((i) => i.id === additionalFilter.id)) {
      return rawFilters;
    }

    return {
      combinator: rawFilters?.combinator!,
      not: rawFilters?.not!,
      rules: [...(rawFilters?.rules ?? []), additionalFilter],
    };
  }, [rawFilters, additionalFilter]);

  const handleFilterChange = useCallback(
    (data: CustomViewFormModel['conditions'] | null) => {
      handleFiltersUpdate(data);

      if (additionalFilter) {
        setAdditionalFilter(null);
      }
    },
    [handleFiltersUpdate, additionalFilter],
  );

  useEffect(() => {
    if (!entityName) {
      setSelectedEntity(EntityNames.Conversation, true);
    }
  }, [entityName, setSelectedEntity]);

  return (
    <Box sx={CONVERSATIONS_VIEW_WRAPPER_STYLES}>
      <MetaTags title="Search" />
      <BlockContainer
        sx={{
          border: theme.utils.important('none'),
        }}
      >
        <BlockContent sx={{ padding: 0 }}>
          <RecordsGridLayout<AnySearchableEntity>
            leftPanelWidth={
              entityName !== EntityNames.Conversation
                ? INITIAL_GRID_WIDTH_WITHOUT_PANEL
                : undefined
            }
            resetToDefault={resetToDefault}
            entityName={entityName}
            isRecordsLoading={isLoading}
            isRecordsLoadingMore={isLoadingMore}
            attributesList={attributesList}
            isQueryBuilderOpen={isQueryBuilderOpen}
            sort={sort}
            filters={filters!}
            columns={columns!}
            formMethods={formMethods}
            handleColumnUpdate={handleColumnUpdate}
            handleFilterChange={handleFilterChange}
            handleColumnsUpdate={handleColumnsUpdate}
            handleSortChange={handleSortChange}
            toggleQueryBuilder={toggleQueryBuilder}
            handleScroll={handleScroll}
            records={records!}
            totalRecords={totalRecords}
            selectedRecord={selectedRecord}
            onRecordClick={handleRecordClick}
            onRecordClose={handleRecordClose}
            slots={{
              rightPanel: (
                <GenericSidePanel
                  selectedRecord={selectedRecord}
                  entityName={entityName}
                  handleRecordClose={handleRecordClose}
                />
              ),
              header: (
                <TabsBar
                  selectedEntity={entityName}
                  tabs={tabs}
                  onEntityClick={setSelectedEntity}
                />
              ),
              subHeader: (
                <SearchBar
                  onSearch={handleSearch}
                  value={searchTerm}
                  slotProps={{ htmlInput: { 'data-testid': 'search-page-input' } }}
                />
              ),
            }}
          />
        </BlockContent>
      </BlockContainer>

      <GenericModalSet entityName={entityName} selectedRecord={selectedRecord} />
    </Box>
  );
};

function getSearchViewId(entity: string) {
  return `s:${entity}`;
}
